import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ProductHybridMain } from "../hybridPage";
import { setPageTitle } from "../../../utils/pageTitle";
import { clearGridPosition } from "../../../utils/categoryRestore";
import { useOverlayShell } from "./useOverlayShell";
import { BACKGROUND_LOCATION_KEY, PRODUCT_LAYERS_KEY, ProductPageOverlayContext } from "./productPageOverlayContext";

const preventHashOnlyNavigation = (event) => {
  const link = event.target.closest?.("a[href]");
  if (link && link.getAttribute("href") === "#") event.preventDefault();
};

const href = (location) => `${location.pathname}${location.search ?? ""}${location.hash ?? ""}`;

export const ProductPageOverlay = ({ leaving = false, depth = 0, isTop = true, background, below = [], layerKey }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const closeRef = useRef(null);
  const [title, setTitle] = useState(null);

  const onClose = useCallback(() => {
    if (leaving) return;
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    const previous = below[below.length - 1];
    if (previous) {
      navigate(href(previous), {
        replace: true,
        state: { [BACKGROUND_LOCATION_KEY]: background, [PRODUCT_LAYERS_KEY]: below.slice(0, -1) },
      });
    } else {
      navigate(href(background), { replace: true, state: background.state ?? null });
    }
  }, [leaving, location.key, navigate, below, background]);

  const { container, close } = useOverlayShell({ onClose, closeRef, leaving });
  const visible = isTop && !leaving;

  useEffect(() => clearGridPosition(), []);

  useEffect(() => {
    if (visible && title) setPageTitle(title);
  }, [visible, title]);

  const backLink = useMemo(
    () => (
      <div className="backAccount myAccount-back d-flex">
        <a
          href="#"
          ref={closeRef}
          role="button"
          aria-label="Back"
          onClick={(event) => {
            event.preventDefault();
            close();
          }}
        >
          <i className="fa-solid fa-chevron-left" /> Back
        </a>
      </div>
    ),
    [close],
  );

  const overlayValue = useMemo(() => ({ backLink, setTitle, depth, isTop }), [backLink, depth, isTop]);

  return createPortal(
    <ProductPageOverlayContext.Provider value={overlayValue}>
      <div
        className="product-layer"
        data-layer-key={layerKey}
        hidden={!visible}
        role="dialog"
        aria-label={title || "Product details"}
        onClickCapture={preventHashOnlyNavigation}
      >
        <ProductHybridMain />
      </div>
    </ProductPageOverlayContext.Provider>,
    container,
  );
};
